// Serviço para gerenciar níveis de acesso de usuários em rotas
// ui/src/services/routeAccessService.ts

import api from '@/config/axios';

export interface RouteAccessData {
    user: {
        id: number;
        sAMAccountName: string;
        display_name: string;
    };
    route: {
        id: number;
        path: string;
        title: string;
    };
    accessLevel: 'none' | 'member' | 'editor' | 'admin';
    defaultLevel: 'none' | 'member' | 'editor' | 'admin';
    isCustom: boolean;
}

export interface UpdateAccessLevelRequest {
    accessLevel: 'none' | 'member' | 'editor' | 'admin';
}

class RouteAccessService {
    private baseURL = '/user-route-levels';

    /**
     * Busca o nível de acesso atual de um usuário em uma rota específica
     */
    async getUserRouteLevel(samaccountname: string, routePath: string): Promise<RouteAccessData | null> {
        try {
            // Usar validateStatus para não rejeitar 404 automaticamente (evita erro no console)
            const response = await api.get(`${this.baseURL}/users/${samaccountname}/routes/${encodeURIComponent(routePath)}`, {
                validateStatus: (status) => {
                    // Aceitar 404 como resposta válida (não é erro neste caso)
                    return status === 200 || status === 404;
                }
            });

            // Se retornou 404, a rota não existe no banco (sem permissões customizadas)
            if (response.status === 404) {
                return null;
            }

            return response.data.data;
        } catch (error: any) {
            // Em caso de erro inesperado, logar mas não bloquear acesso (fail-open)
            if (error.response?.status !== 404 && !error.isExpected404) {
                console.error('Erro ao buscar nível de acesso:', error);
            }
            return null;
        }
    }

    /**
     * Atualiza o nível de acesso de um usuário em uma rota específica
     */
    async updateUserRouteLevel(
        samaccountname: string,
        routePath: string,
        accessLevel: 'none' | 'member' | 'editor' | 'admin'
    ): Promise<RouteAccessData> {
        try {
            const response = await api.put(`${this.baseURL}/users/${samaccountname}/routes/${encodeURIComponent(routePath)}`, {
                accessLevel
            });
            return response.data.data;
        } catch (error: any) {
            console.error('Erro ao atualizar nível de acesso:', error);
            throw new Error(error.response?.data?.message || 'Erro ao atualizar nível de acesso');
        }
    }

    /**
     * Define automaticamente o nível de acesso baseado no status do usuário
     * Esta função busca o nível correto do banco de dados baseado na lógica implementada
     */
    async setAccessLevelByStatus(
        samaccountname: string,
        routePath: string,
        userStatus?: 'member' | 'editor' | 'admin'
    ): Promise<RouteAccessData> {
        try {
            console.log(`🔄 Definindo nível de acesso: ${samaccountname} -> ${routePath}`);

            // Se userStatus não foi fornecido, busca o nível correto do banco
            let accessLevel: 'none' | 'member' | 'editor' | 'admin';

            if (userStatus) {
                // Se foi fornecido um status específico, usa ele
                accessLevel = userStatus;
            } else {
                // Busca o nível atual do banco (que já tem a lógica correta implementada)
                const currentData = await this.getUserRouteLevel(samaccountname, routePath);
                if (currentData) {
                    accessLevel = currentData.accessLevel;

                    // Se o nível atual é 'none', define como 'member' por padrão
                    if (accessLevel === 'none') {
                        accessLevel = 'member';
                    }
                } else {
                    // Se não houver dados, definir como 'member' por padrão
                    accessLevel = 'member';
                }
            }

            const result = await this.updateUserRouteLevel(samaccountname, routePath, accessLevel);

            console.log(`✅ Nível de acesso definido: ${result.accessLevel}`);
            return result;
        } catch (error: any) {
            console.error('Erro ao definir nível de acesso:', error);
            throw error;
        }
    }

    /**
     * Verifica se o usuário tem acesso a uma rota específica
     */
    async checkRouteAccess(samaccountname: string, routePath: string): Promise<boolean> {
        try {
            const data = await this.getUserRouteLevel(samaccountname, routePath);
            return data ? data.accessLevel !== 'none' : false;
        } catch (error) {
            console.error('Erro ao verificar acesso à rota:', error);
            return false;
        }
    }

    /**
     * Obtém todos os níveis de acesso de um usuário
     */
    async getAllUserRouteLevels(samaccountname: string): Promise<any> {
        try {
            const response = await api.get(`${this.baseURL}/users/${samaccountname}`);
            return response.data.data;
        } catch (error: any) {
            console.error('Erro ao buscar todos os níveis de acesso:', error);
            throw new Error(error.response?.data?.message || 'Erro ao buscar níveis de acesso');
        }
    }

    /**
     * Obtém permissões administrativas do usuário (apenas rotas admin/editor)
     * Usado para verificar quais áreas administrativas o usuário pode ver no menu
     */
    async getAdminPermissions(samaccountname: string): Promise<Map<string, 'admin' | 'editor'>> {
        try {
            const response = await api.get(`${this.baseURL}/users/${samaccountname}/admin-permissions`);
            const data = response.data.data;

            // Se é admin master, retornar mapa vazio (tem acesso a tudo)
            if (data.isAdminMaster) {
                return new Map();
            }

            // Converter objeto de permissões para Map
            const permissionsMap = new Map<string, 'admin' | 'editor'>();
            Object.entries(data.permissions || {}).forEach(([path, level]) => {
                permissionsMap.set(path, level as 'admin' | 'editor');
            });

            return permissionsMap;
        } catch (error: any) {
            console.error('Erro ao buscar permissões administrativas:', error);
            // Em caso de erro, retornar mapa vazio (fail-safe)
            return new Map();
        }
    }

    /**
     * Sincroniza rotas do frontend com o banco de dados.
     * Chamado automaticamente no login para garantir que todas as rotas existam no banco.
     */
    async syncRoutes(routes: Array<{ path: string; name?: string; title?: string; requiresAuth?: boolean; requiresAdminMaster?: boolean }>): Promise<void> {
        try {
            await api.post('/routes/sync', { routes });
        } catch (error: any) {
            console.error('Erro ao sincronizar rotas:', error);
            // Não lançar erro - sync é best-effort, não deve bloquear o login
        }
    }
}

// Instância singleton do serviço
export const routeAccessService = new RouteAccessService();

// Função utilitária para definir nível de acesso automaticamente
export const setUserRouteAccess = async (samaccountname: string, routePath: string, level: 'member' | 'editor' | 'admin') => {
    try {
        return await routeAccessService.setAccessLevelByStatus(samaccountname, routePath, level);
    } catch (error) {
        console.error('Erro ao definir acesso do usuário:', error);
        throw error;
    }
};

// Função utilitária para verificar acesso
export const checkUserRouteAccess = async (samaccountname: string, routePath: string) => {
    try {
        return await routeAccessService.checkRouteAccess(samaccountname, routePath);
    } catch (error) {
        console.error('Erro ao verificar acesso:', error);
        return false;
    }
};
